import { useState, useEffect, useRef } from 'react';
import { extractYouTubeVideoId } from '@/utils/youtube-url';
import { checkVideoLiveStatus, type YouTubeStreamStatus } from '@/services/youtube.api';

const DEFAULT_POLL_INTERVAL_MS = 30_000;

interface UseYouTubeLiveStatusReturn {
  status: YouTubeStreamStatus;
}

/**
 * Periodically checks whether a YouTube stream is currently live.
 * Returns status: 'live' | 'vod' | 'error'.
 * 'error' is also returned when youtubeURL is empty or invalid.
 */
export function useYouTubeLiveStatus(
  youtubeURL: string,
  pollingIntervalMs: number = DEFAULT_POLL_INTERVAL_MS,
): UseYouTubeLiveStatusReturn {
  const [status, setStatus] = useState<YouTubeStreamStatus>('error');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    const videoId = extractYouTubeVideoId(youtubeURL);
    if (videoId === null) {
      setStatus('error');
      return;
    }

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const poll = async () => {
      const result = await checkVideoLiveStatus(videoId);
      if (mountedRef.current) {
        setStatus(result.status);
      }
    };

    poll();
    intervalId = setInterval(poll, pollingIntervalMs);

    return () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
    };
  }, [youtubeURL, pollingIntervalMs]);

  return { status };
}
